/**
 * DATEV "Buchungsstapel" (EXTF version 700, category 21, format version 13) for Austrian
 * clients. The same format as in Germany; only the accounts and BU-Schlüssel differ.
 *
 *  - Line 1 is the batch header (Berater, Mandant, fiscal year, account length, date range),
 *    line 2 the 125 column names (`DATEV_COLUMNS`), then one booking per line. Windows-1252,
 *    CR/LF, ";" between fields, text in double quotes, decimal comma, no negative amounts.
 *  - A booking is the gross on the Personenkonto (or Kassa) against the expense or revenue
 *    account, the direction in "Soll/Haben-Kennzeichen" (ER/KA "H", AR "S"), and the BU-Schlüssel
 *    telling DATEV which tax to split off. Several rates are several bookings with the same
 *    Belegfeld 1; a tip is a booking without key.
 *  - Reverse charge and ig. Erwerb book the net; the key makes DATEV owe and deduct the tax.
 *  - Belegdatum carries day and month only, the year comes from the header, and a booking outside
 *    the batch's date range is refused - so the date is the one that put the document into the
 *    period (`relevantDate`), and a batch never spans two fiscal years.
 *  - Belege cannot be linked from a file (Beleglink needs a DATEV document GUID); the PDFs travel
 *    in the ZIP named by Belegfeld 1.
 */
import type { DocumentRecord } from "@/lib/api/documents";
import type { Period } from "@/lib/at/periods";
import { relevantDate } from "@/lib/at/uva";
import {
  clean,
  dateParts,
  isVatId,
  prepareBookings,
  type BookingContext,
  type BookingProblem,
} from "./common";
import { DATEV_COLUMNS } from "./datev-columns";
import type { PlainBooking } from "./money-bookings";
import { DATEV_RATES, datevKey, type DatevKeyId, type ExportSettings } from "./settings";

const quote = (value: string) => `"${value.replace(/"/g, '""')}"`;
const amount = (value: number) => Math.abs(value).toFixed(2).replace(".", ",");

/** DDMM, the year coming from the batch header. */
function ddmm(value: string | undefined): string {
  const parts = dateParts(value);
  return parts ? `${parts[0]}${parts[1]}` : "";
}

/** Belegfeld 1 allows A-Z a-z 0-9 $ & % * + - / and 36 characters. */
export function belegfeld(value: string): string {
  return value.replace(/[^A-Za-z0-9$&%*+\-/]/g, "").slice(0, 36);
}

/** The key id for a line, or null when the booking carries no tax at all. */
function keyId(
  direction: "in" | "out",
  treatment: NonNullable<DocumentRecord["taxTreatment"]>,
  rate: number,
): DatevKeyId | null | undefined {
  const known = (DATEV_RATES as readonly number[]).includes(rate);
  if (treatment === "standard") {
    if (rate === 0) return null;
    return known ? (`${direction}.standard.${rate}` as DatevKeyId) : undefined;
  }
  if (
    direction === "in" &&
    (treatment === "eu_intra_community" || treatment === "reverse_charge")
  ) {
    return known ? (`in.${treatment}.${rate}` as DatevKeyId) : undefined;
  }
  if (
    direction === "out" &&
    (treatment === "export" || treatment === "eu_intra_community" || treatment === "reverse_charge")
  ) {
    return `out.${treatment}`;
  }
  // Kleinunternehmer, no VAT, an invoice from outside the EU: nothing for DATEV to split off.
  return null;
}

export function datevBookings(
  documents: DocumentRecord[],
  context: BookingContext,
): { rows: string[][]; problems: BookingProblem[] } {
  const prepared = prepareBookings(documents, context);
  const problems = [...prepared.problems];
  const rows: string[][] = [];

  for (const { document, account, counterAccount, lines, tip } of prepared.bookable) {
    const direction = document.kind === "AR" ? "out" : "in";
    const treatment = document.taxTreatment ?? "standard";
    const vatFree = treatment !== "standard";

    const booked: Array<{ amount: number; key: string; rate: number }> = [];
    let missing = false;
    for (const line of lines) {
      const id = keyId(direction, treatment, line.rate);
      const key = id === null ? "" : id === undefined ? "" : datevKey(context.settings, id);
      if (id === undefined || (id !== null && !key)) missing = true;
      booked.push({ amount: vatFree ? line.net : line.net + line.vat, key, rate: line.rate });
    }
    if (missing) {
      problems.push({
        documentId: document.id,
        docNumber: document.docNumber ?? "",
        reason: "no_tax_key",
      });
      continue;
    }
    if (tip > 0) booked.push({ amount: tip, key: "", rate: 0 });

    const vatId = (document.partnerVatId ?? "").replace(/\s+/g, "").toUpperCase();
    const euVatId =
      (treatment === "eu_intra_community" ||
        (direction === "out" && treatment === "reverse_charge")) &&
      isVatId(vatId)
        ? vatId
        : "";

    for (const line of booked) {
      const fields = new Array<string>(DATEV_COLUMNS.length).fill("");
      fields[0] = amount(line.amount);
      fields[1] = quote(direction === "out" ? "S" : "H");
      fields[2] = quote("EUR");
      fields[6] = counterAccount;
      fields[7] = account;
      fields[8] = line.key ? quote(line.key) : "";
      fields[9] = ddmm(relevantDate(document, context.basis) || document.issueDate);
      fields[10] = quote(belegfeld(document.docNumber ?? ""));
      fields[13] = quote(
        clean(`${document.partnerName ?? ""} ${document.invoiceNumber ?? ""}`, 60),
      );
      if (euVatId) {
        fields[39] = quote(euVatId);
        if (treatment === "eu_intra_community" && line.rate) {
          fields[40] = line.rate.toFixed(2).replace(".", ",");
        }
      }
      fields[113] = "0"; // Festschreibung: pre-accounting, the Kanzlei locks it.
      rows.push(fields);
    }
  }
  return { rows, problems };
}

/** Problems with the batch as a whole, before any document is looked at. */
export type DatevBatchProblem = "no_client_numbers" | "spans_fiscal_years";

/** First day of the fiscal year a date falls into, e.g. 2026-01-01 or 2025-07-01. */
export function fiscalYearStart(date: string, startMonth: number): string {
  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7));
  const startYear = month >= startMonth ? year : year - 1;
  return `${startYear}-${String(startMonth).padStart(2, "0")}-01`;
}

export function datevBatchProblems(
  settings: ExportSettings,
  period: Period,
  startMonth: number,
): DatevBatchProblem[] {
  const problems: DatevBatchProblem[] = [];
  const berater = Number(settings.datev.berater);
  const mandant = Number(settings.datev.mandant);
  if (!(berater >= 1001 && berater <= 9999999 && mandant >= 1 && mandant <= 99999)) {
    problems.push("no_client_numbers");
  }
  if (fiscalYearStart(period.from, startMonth) !== fiscalYearStart(period.to, startMonth)) {
    problems.push("spans_fiscal_years");
  }
  return problems;
}

const compact = (date: string) => date.slice(0, 10).replace(/-/g, "");

/** Header line: "EXTF";700;21;"Buchungsstapel";13;created;... - 31 fields. */
export function datevHeader(input: {
  settings: ExportSettings;
  period: Period;
  startMonth: number;
  created: Date;
  label: string;
}): string {
  const created = input.created;
  const pad = (value: number, length = 2) => String(value).padStart(length, "0");
  const stamp =
    `${created.getFullYear()}${pad(created.getMonth() + 1)}${pad(created.getDate())}` +
    `${pad(created.getHours())}${pad(created.getMinutes())}${pad(created.getSeconds())}` +
    pad(created.getMilliseconds(), 3);
  const fields = [
    quote("EXTF"),
    "700",
    "21",
    quote("Buchungsstapel"),
    "13",
    stamp,
    "",
    quote("PB"),
    quote("PrestoBooks"),
    "",
    input.settings.datev.berater,
    input.settings.datev.mandant,
    compact(fiscalYearStart(input.period.from, input.startMonth)),
    String(input.settings.datev.accountLength),
    compact(input.period.from),
    compact(input.period.to),
    quote(clean(input.label, 30)),
    "",
    "1", // Finanzbuchführung
    "",
    "0", // not locked: the Kanzlei checks first
    quote("EUR"),
    ...new Array<string>(9).fill(""),
  ];
  return fields.join(";");
}

/** Header, column names, bookings, CR/LF after every line. Encode with `encodeCp1252`. */
export function datevFile(header: string, rows: string[][]): string {
  return (
    [header, DATEV_COLUMNS.join(";"), ...rows.map((row) => row.join(";"))].join("\r\n") + "\r\n"
  );
}

/**
 * Bookings between two Sachkonten (`moneyBookings`): "S" on the debit account against the credit
 * account, no BU-Schlüssel.
 */
export function datevPlainRows(bookings: PlainBooking[]): string[][] {
  return bookings.map((booking) => {
    const fields = new Array<string>(DATEV_COLUMNS.length).fill("");
    fields[0] = amount(booking.amount);
    fields[1] = quote("S");
    fields[2] = quote("EUR");
    fields[6] = booking.debit;
    fields[7] = booking.credit;
    fields[9] = ddmm(booking.date);
    fields[10] = quote(belegfeld(`${booking.symbol}${booking.belegnr}`));
    fields[13] = quote(clean(booking.text, 60));
    fields[113] = "0";
    return fields;
  });
}
